import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Modal } from '@shape-ds/components';
import { AxiosError } from 'axios';
import { RadarView } from 'queries/scalings/contract';
import { Dayjs } from 'lib';
import { InfoMessage } from '@shape/lighthouse-components';
import { ExpandIcon } from 'components/ExpandIcon/ExpandIcon';

import './style.scss';
import { IconButton, Slider } from '@mui/material';
import { Pause, PlayArrow, Stop } from '@mui/icons-material';
import useRadarViewChart from './useRadarViewChart';

export type RadarViewChartProps = {
  dateRange: { start: Dayjs; end: Dayjs };
  initialValue?: Dayjs;
  hasTitle?: boolean;
  hasExpandIcon?: boolean;
  data?: RadarView;
  isLoading?: boolean;
  error?: AxiosError<unknown, any> | null;
};

export const RadarViewChart = ({
  data,
  isLoading,
  error,
  dateRange,
  initialValue,
  hasTitle = true,
  hasExpandIcon = true,
}: RadarViewChartProps) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);

  let echartRef: ReactECharts | null;

  const {
    sliderValue,
    sliderItemsQty,
    isPlayingAnimation,
    options,
    handleChange,
    handleValueLabelFormat,
    playPauseAnimation,
    stopAnimation,
  } = useRadarViewChart({ data, initialValue });

  useEffect(() => {
    if (echartRef) {
      echartRef.getEchartsInstance().setOption(options);
    }
  });

  if (error || !data) {
    return error?.response?.status === 404 ? (
      <div className="scaling-error-container">
        <InfoMessage
          type="warning"
          title={t('messages:model_not_found.title')}
          message={t('messages:model_not_found.message')}
        />
      </div>
    ) : (
      <div className="scaling-error-container">
        <InfoMessage
          type="error"
          title={t('messages:error.title')}
          message={t('messages:error.message')}
        />
      </div>
    );
  }

  const renderGraph = (isInsideModal?: boolean) => (
    <>
      {hasTitle && (
        <h1 className="is-size-5 has-text-weight-bold p-0 pl-2 mb-6">
          {t('components:radar_graphic.title')}
        </h1>
      )}
      {!options.radar.indicator && (
        <div className="scaling-error-container">
          <InfoMessage
            type="warning"
            title={t('components:radar_graphic.message.no_data.title')}
            message={t('components:radar_graphic.message.no_data.message')}
            className="fallback-msg"
          />
        </div>
      )}
      {options.radar.indicator.length > 0 && (
        <div className="columns mb-0">
          <div
            className="column pb-0"
            style={{
              marginLeft: '-10px',
              width: '100%',
            }}
          >
            <ReactECharts
              ref={(e) => {
                echartRef = e;
              }}
              echarts={echarts}
              style={{ height: isInsideModal ? '550px' : '420px', width: '100%' }}
              option={options}
            />
          </div>
        </div>
      )}
      <div className="columns mt-2">
        <div className="column is-2 mt-2">
          <IconButton color="primary" onClick={playPauseAnimation}>
            {isPlayingAnimation ? <Pause /> : <PlayArrow />}
          </IconButton>
          <IconButton color="primary" disabled={!isPlayingAnimation} onClick={stopAnimation}>
            <Stop />
          </IconButton>
        </div>
        <div className="column is-10">
          <small className="radialScaling-dates is-pulled-right">
            {dateRange.start.format('DD/MM/YYYY')} - {dateRange.end.format('DD/MM/YYYY')}
          </small>
          <Slider
            className="radialScaling-slider"
            valueLabelDisplay="on"
            size="small"
            marks
            min={0}
            max={sliderItemsQty}
            value={sliderValue}
            onChange={handleChange}
            valueLabelFormat={handleValueLabelFormat}
          />
        </div>
      </div>
    </>
  );
  return (
    <>
      {options.radar.indicator && hasExpandIcon && (
        <ExpandIcon onClick={() => setIsModalVisible(true)} />
      )}
      <div className="radialScaling">{renderGraph()}</div>
      <Modal open={isModalVisible} onClose={() => setIsModalVisible(false)}>
        {renderGraph(true)}
      </Modal>
    </>
  );
};
